import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Card, Switch, Space, InputNumber, Typography, message, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, AppstoreOutlined } from '@ant-design/icons';
import { categoriesAPI } from '../services/api';

const { Title } = Typography;
const { TextArea } = Input;

export default function CategoryForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const colorValue = Form.useWatch('color', form);

    const fetchCategory = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            const response = await categoriesAPI.getById(id);
            form.setFieldsValue(response.data);
        } catch (error) {
            console.error('Error fetching category:', error);
            message.error('Failed to fetch category');
        } finally {
            setLoading(false);
        }
    }, [id, form]);

    const generateSlug = (text) => {
        if (!text) return '';
        return text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    };

    const handleFormChange = (changedValues) => {
        if (changedValues.name !== undefined) {
            const newSlug = generateSlug(changedValues.name);
            form.setFieldsValue({ slug: newSlug });
        }
    };

    useEffect(() => {
        if (isEdit) {
            fetchCategory();
        }
    }, [isEdit, fetchCategory]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            if (isEdit) {
                await categoriesAPI.update(id, values);
                message.success('Category updated!');
            } else {
                await categoriesAPI.create(values);
                message.success('Category created!');
            }
            navigate('/categories');
        } catch (error) {
            message.error('Error: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/categories')} style={{ marginBottom: 16 }}>
                    Back to Categories
                </Button>
                <Title level={2}><AppstoreOutlined /> {isEdit ? 'Edit' : 'Add'} Category</Title>
            </div>

            <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onValuesChange={handleFormChange}
                    initialValues={{ order: 0, isActive: true }}
                >
                    <Form.Item
                        name="name"
                        label="Category Name"
                        rules={[{ required: true, message: 'Please enter category name' }]}
                    >
                        <Input size="large" placeholder="e.g. Bangunan Gedung" />
                    </Form.Item>

                    <Form.Item
                        name="slug"
                        label="Slug"
                        rules={[{ required: true, message: 'Slug is required' }]}
                    >
                        <Input size="large" placeholder="e.g. bangunan-gedung" />
                    </Form.Item>

                    <Form.Item name="description" label="Description">
                        <TextArea rows={3} placeholder="Category description" />
                    </Form.Item>

                    <Form.Item name="icon" label="Icon Name">
                        <Input size="large" placeholder="e.g. building, bridge, infrastructure" />
                    </Form.Item>

                    <Form.Item
                        name="color"
                        label="Color (Hex Code)"
                        extra="Enter hex color code for the category (e.g. #8B4513)"
                    >
                        <Input
                            size="large"
                            placeholder="#8B4513"
                            addonBefore={
                                <div style={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: colorValue || '#ccc',
                                    borderRadius: '4px'
                                }} />
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name="order"
                        label="Display Order"
                        extra="Lower numbers appear first"
                    >
                        <InputNumber size="large" style={{ width: '100%' }} min={0} />
                    </Form.Item>

                    <Form.Item name="isActive" label="Status" valuePropName="checked">
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 32 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button size="large" onClick={() => navigate('/categories')} style={{ minWidth: 120 }}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={submitting}
                                style={{
                                    minWidth: 120,
                                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                    border: 'none'
                                }}
                            >
                                {isEdit ? 'Update' : 'Create'} Category
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
}
